import type { SharedApiClient } from '../api/client';
import type {
  WalletBalance,
  WalletTransaction,
  CheckoutSessionResponse,
  VerifySessionResponse,
  RedeemCouponResponse,
} from '../api/types';

export const ANALYSIS_PRICE_CENTS = 4999;

/** Mirrors backend ``PRODUCT_PRICES_CENTS`` / ``resolve_product_type`` (billing_enums.py). */
export const PRODUCT_PRICES_CENTS: Record<string, number> = {
  feature_film_screenplay: 4999,
  feature_film_writer_coverage: 1999,
  feature_film_treatment: 999,
  feature_film_outline: 999,
  short_film_screenplay: 0,
  // Unified TV full-analysis product (July 2026) -- one credit covers a
  // pilot, a pilot + bible, or a full season of any episode count.
  tv_series: 3999,
  // Legacy episode-count-locked TV SKUs, no longer sold. Kept only so old
  // wallets/receipts referencing them still render correctly.
  tv_pilot_bible: 3999,
  tv_series_4_6: 6999,
  tv_series_7_12: 8999,
  tv_pitch: 1999,
  stage_play: 2999,
  pitch_deck: 999,
  territorial_censorship: 1599,
};

/**
 * Credit "families" -- product types that are fully interchangeable when
 * spending a credit. Mirrors backend ``PRODUCT_CREDIT_FAMILIES``
 * (billing_enums.py). Used to show a correct combined credit count for
 * customers who still hold legacy TV credits alongside the new unified
 * ``tv_series`` credits.
 */
export const PRODUCT_CREDIT_FAMILIES: Record<string, string[]> = {
  tv_series: ['tv_series', 'tv_pilot_bible', 'tv_series_4_6', 'tv_series_7_12'],
  tv_pilot_bible: ['tv_series', 'tv_pilot_bible', 'tv_series_4_6', 'tv_series_7_12'],
  tv_series_4_6: ['tv_series', 'tv_pilot_bible', 'tv_series_4_6', 'tv_series_7_12'],
  tv_series_7_12: ['tv_series', 'tv_pilot_bible', 'tv_series_4_6', 'tv_series_7_12'],
};

/** Sum available credits for ``productType`` across its whole credit family. */
export function sumProductCredits(
  productType: string,
  credits: Record<string, number> | null | undefined,
): number {
  const family = PRODUCT_CREDIT_FAMILIES[productType] ?? [productType];
  return family.reduce((sum, key) => sum + (credits?.[key] ?? 0), 0);
}

export type ProductPricing = {
  productType: string;
  priceCents: number;
};

/** Single frontend resolver — keep in sync with backend ``resolve_product_type``. */
export function resolveProductType(params: {
  contentType?: string | null;
  detectedFormat?: string | null;
  documentType?: string | null;
  episodeCount?: number | null;
  analysisFocus?: string | null;
}): ProductPricing {
  const ct = (params.contentType ?? '').toLowerCase();
  const fmt = (params.detectedFormat ?? '').toLowerCase();
  const doc = (params.documentType ?? '').toLowerCase();
  const focus = (params.analysisFocus ?? '').toLowerCase();
  // episodeCount is accepted for backward compat with existing callers but
  // intentionally unused -- see the July 2026 TV simplification note below.

  let productType: string;

  if (ct === 'stage_play') {
    productType = 'stage_play';
  } else if (ct === 'tv_series') {
    // July 2026 simplification: one TV credit works for a pilot, a pilot +
    // bible, or a full season of any episode count -- episodeCount is
    // intentionally ignored. A treatment/pitch-deck-style TV document is
    // still a distinct (cheaper) product.
    if (doc === 'treatment' || doc === 'pitch') {
      productType = 'tv_pitch';
    } else {
      productType = 'tv_series';
    }
  } else if (fmt === 'tv_pilot') {
    productType = 'tv_series';
  } else if (fmt === 'short_film') {
    productType = 'short_film_screenplay';
  } else if (doc === 'outline') {
    productType = 'feature_film_outline';
  } else if (doc === 'treatment' || doc === 'pitch') {
    productType = 'feature_film_treatment';
  } else if (focus === 'writer') {
    productType = 'feature_film_writer_coverage';
  } else {
    productType = 'feature_film_screenplay';
  }

  return {
    productType,
    priceCents: PRODUCT_PRICES_CENTS[productType] ?? ANALYSIS_PRICE_CENTS,
  };
}

export type InsufficientBalanceDetail = {
  balanceCents: number;
  requiredCents: number;
  productType: string;
  freeAnalysisQuota: number;
  productCredits: Record<string, number>;
};

/** Normalize a backend 402 ``insufficient_balance`` / ``insufficient_credits`` payload. */
export function parseInsufficientBalanceDetail(
  detail: Record<string, unknown> | null | undefined,
  fallback: Partial<InsufficientBalanceDetail> = {},
): InsufficientBalanceDetail {
  return {
    balanceCents: Number(detail?.balance_cents ?? fallback.balanceCents ?? 0),
    requiredCents: Number(detail?.required_cents ?? fallback.requiredCents ?? 999),
    productType: String(detail?.product_type ?? fallback.productType ?? 'feature_film_screenplay'),
    freeAnalysisQuota: Number(detail?.free_analysis_quota ?? fallback.freeAnalysisQuota ?? 0),
    productCredits: (detail?.product_credits as Record<string, number>) ?? fallback.productCredits ?? {},
  };
}

export function createBillingHelpers(client: SharedApiClient) {
  async function getWalletBalance(): Promise<WalletBalance> {
    return client.get<WalletBalance>('/billing/wallet');
  }

  async function getTransactionHistory(
    limit = 50,
    offset = 0,
  ): Promise<WalletTransaction[]> {
    return client.get<WalletTransaction[]>(
      `/billing/transactions?limit=${limit}&offset=${offset}`,
    );
  }

  async function createCheckoutSession(
    amount_cents: number,
    success_url: string,
    cancel_url: string,
    analyses_count?: number,
    product_type?: string,
    attribution?: Record<string, string>,
    marketing_opt_in?: boolean,
  ): Promise<CheckoutSessionResponse> {
    return client.post<CheckoutSessionResponse>('/billing/checkout/create', {
      amount_cents,
      success_url,
      cancel_url,
      analyses_count,
      product_type,
      attribution,
      ...(marketing_opt_in ? { marketing_opt_in: true } : {}),
    });
  }

  async function verifyCheckoutSession(
    session_id: string,
  ): Promise<VerifySessionResponse> {
    return client.post<VerifySessionResponse>('/billing/checkout/verify', {
      session_id,
    });
  }

  async function verifyIAPReceipt(
    receipt: string,
    transactionId: string,
    productId: string,
  ): Promise<VerifySessionResponse> {
    return client.post<VerifySessionResponse>('/billing/iap/verify', {
      receipt,
      transactionId,
      productId,
    });
  }

  async function getCreditPackages(): Promise<any[]> {
    return client.get('/billing/packages');
  }

  async function redeemCoupon(code: string): Promise<RedeemCouponResponse> {
    return client.post<RedeemCouponResponse>('/billing/redeem-coupon', { code });
  }

  function canAffordAnalysis(wallet: WalletBalance): boolean {
    const ptCredits =
      wallet.product_credits?.full_analysis ??
      wallet.product_credits?.production_tracking ??
      0;
    const creditCount = typeof ptCredits === 'object' ? 0 : ptCredits;
    return (
      wallet.free_analysis_quota > 0 ||
      creditCount > 0 ||
      wallet.balance_cents >= ANALYSIS_PRICE_CENTS
    );
  }

  return {
    getWalletBalance,
    getTransactionHistory,
    createCheckoutSession,
    verifyCheckoutSession,
    verifyIAPReceipt,
    getCreditPackages,
    redeemCoupon,
    canAffordAnalysis,
    ANALYSIS_PRICE_CENTS,
    PRODUCT_PRICES_CENTS,
    resolveProductType,
    parseInsufficientBalanceDetail,
  };
}
